import { ConfPricing, ConfTierPrice, OrderDataPaymentType, OrderDataPaymentTypeDetail, AcquireDataType, 
  HomeAddressInterface, OfficeVisitInterface, ReleaseDataType, PriceType 
} from './order-db-types';
import { effectiveWeight } from '../util/effective-weight'
import toMoney from '../util/tomoney';


export function makeOrderData(o: any, nohistory: boolean = false) {
	const od = new OrderData();

	od.info = new OrderDataInfo(o.info);
	od.payment = new OrderDataPayment(o.payment);

	od.acquire = makeAcquireReleaseData(o.acquire, new AcquireData());
	od.release = makeAcquireReleaseData(o.release, new ReleaseData());

	od.comments = o.comments || "";
	od.total = o.total || 0;

	o.items = Array.isArray(o.items) ? o.items : [];
	od.items = o.items.map((item: any) => new OrderDataItem(item));

	od.ostatus = new OrderDataOStatus(o.ostatus);
	od.flagged = o.flagged;

	if (!nohistory) {
		od.history = [];

		if (o.history && Array.isArray(o.history)) {
			od.history = o.history.map((h: any) => new OrderHistoryEntry(h));
		}
	}

	return od;
}


function makeAcquireReleaseData<S, T extends AcquireData | ReleaseData>(ardata: any, ar: T): T {
	if (ardata.type) {
		ar.type = ardata.type;
    ar.fee = ardata.fee;
	}
	if (ardata.detail) {
		ar.detail = {};
		const addr_fields = ["name", "address1", "address2", "city", "state", "zip", "phone", "requested_time"];
		for (const f of addr_fields) {
			(ar.detail as any)[f] = ardata.detail[f] || "";
		}
	}
	return ar;
}


export class OrderData {

  info: OrderDataInfo;
  payment: OrderDataPayment;
  items: OrderDataItem[] = [];
  acquire: AcquireData;
  release: ReleaseData;
  comments: string = "";
  total: number = 0;
  ostatus: OrderDataOStatus;
  flagged: boolean = false;
  history: OrderHistoryEntry[];


  constructor(clone?: OrderData) {
    if (clone) {
      this.info = new OrderDataInfo(clone.info);
      this.payment = new OrderDataPayment(clone.payment);
      this.acquire = clone.acquire.clone();
      this.release = clone.release.clone();
      this.comments = clone.comments;
      this.total = clone.total;
      this.items = [ ...clone.items ];
      this.ostatus = new OrderDataOStatus(clone.ostatus);
      this.flagged = clone.flagged;
      this.history = clone.history || [];
    } else {
      this.info = new OrderDataInfo();
      this.payment = new OrderDataPayment();
      this.acquire = new AcquireData();
      this.release = new ReleaseData();
      this.ostatus = new OrderDataOStatus();
      this.history = [];
    }
  }


  /*
  addItem(type: string, label: string, tag: string, qty: number, conf: ConfPricing, detail?: OrderDataItemData) {
    this.items.push(new OrderDataItem(type, label, tag, qty, this.info.weight, conf, detail));
  }
  */


  get service() {
    return this.items.find(t => t.type === 'service');
  }


  get urn() {
    return this.items.find(t => t.type === 'urn');
  }


  get nameplate() {
    return this.items.find(t => t.type === 'nameplate');
  }


  get pawprint() {
    return this.items.find(t => t.type === 'pawprint');
  }

  
  get acquire_fee() {
    return this.items.find(t => t.type === 'acquire_fee');
  }


  get release_fee() {
    return this.items.find(t => t.type === 'release_fee');
  }

  get viewing_and_priority_service() {
    return this.items.find(t => t.type === 'viewing_and_priority_service');
  }

  get priority_service() {
    return this.items.find(t => t.type === 'priority_service');
  }


  get viewing_service() {
    return this.items.find(t => t.type === 'viewing_service');
  }

  get after_hours_appointment() {
    return this.items.find(t => t.type === 'after_hours_appointment');
  }

}


export class OrderDataPayment {

  type: OrderDataPaymentType = "later";
  type_detail: OrderDataPaymentTypeDetail = "invoice";
  payment_comment: string = "";
  cash_amount?: string;
  check_number?: string;


  constructor(clone?: OrderDataPayment) {
    if (clone) {
      for(const [k,v] of Object.entries(clone)) {
        (this as any)[k] = v;
      }
    }
  }

}


export class OrderDataInfo {

  pet_name: string = "";
  pet_species: string = "";
  weight: number = 0;
  owner_name: string = "";
  owner_phone: string = "";
  owner_email: string = "";


  constructor(clone?: OrderDataInfo) {
    if (clone) {
      for(const [k,v] of Object.entries(clone)) {
        (this as any)[k] = v;
      }
    }
  }

}


export class AcquireData {
  
  type: AcquireDataType = "standard";
  fee: number = 0;
  detail: HomeAddressInterface | OfficeVisitInterface | undefined;

  clone() {
    const c = new AcquireData();
    for(const [k,v] of Object.entries(this)) {
      (this as any)[k] = v;
    }
    return c;
  }

}


export class ReleaseData {
  
  type: ReleaseDataType = "standard";
  fee: number = 0;
  detail: HomeAddressInterface | OfficeVisitInterface | undefined;


  clone() {
    const c = new ReleaseData();
    for(const [k,v] of Object.entries(this)) {
      (this as any)[k] = v;
    }
    return c;
  }

}


export class OrderDataItem {

  type: string = "";
  label: string = "";
  tag: string = "";
  price: number = 0;
  qty: number = 1;
  priceData;
  data?: OrderDataItemData | undefined;


  constructor(itemData: any) {
    this.type = itemData.type;
    this.label = itemData.label;
    this.tag = itemData.tag;
    this.qty = itemData.qty;
    this.price = itemData.price;
    this.priceData = new OrderDataItemPriceData(itemData.priceData);

    if (itemData.data) {
      this.data = new OrderDataItemData(itemData.data);
    }
  }

}


export class OrderDataItemPriceData {


  descriptor?: string | undefined = "";
  price_per_unit?: number | undefined = 0;
  price_per_additional_unit?: number | undefined = 0;
  units?: number | undefined = 0;
  total: number = 0;
  total_additional: number = 0;
  type: PriceType = "fixed";


  constructor(pdata: any) {
    this.descriptor = pdata.descriptor;
    this.price_per_unit = pdata.price_per_unit;
    this.price_per_additional_unit = pdata.price_per_additional_unit;
    this.units = pdata.units;
    this.total = pdata.total;
    this.total_additional = pdata.total_additional;
    this.type = pdata.type;
  }


  getTotal() {
    return this.total;
  }


  getTotalAdditional() {
    return this.total_additional;
  }


  setPricing(weight: number, pconf: ConfPricing, serviceTag?: string): void {

    weight = weight < 0 ? 0 : weight;
    const effective_weight = effectiveWeight(weight);

    this.type = pconf.pricetype;

    if (pconf.pricetype === 'fixed') {
      this.price_per_unit = pconf.price || 0;
      this.total = this.price_per_unit;

    } if (pconf.pricetype === 'fixed_additional') {
      this.price_per_unit = pconf.price || 0;
      this.price_per_additional_unit = pconf.price_additional || 0;
      this.total = this.price_per_unit;
      this.total_additional = this.price_per_additional_unit;
  
    } else if (pconf.pricetype === 'byweight' && pconf.price) {
      this.price_per_unit = pconf.price;
      this.units = effective_weight;
      this.total = pconf.price * effective_weight;
      this.descriptor = `${toMoney(pconf.price)}/lb x ${effective_weight}lbs`;

    } else if (pconf.pricetype === 'tiered' && pconf.prices) {

      let rangeBottom = 1;
      let rangeTop = 0;
      let myprice: ConfTierPrice | undefined;

      // match the price tier with the weight
      for (let i = 0; i < pconf.prices!.length; i++) {
        let pt = pconf.prices![i];
        rangeTop = pt.max ? pt.max : 0;
        if (!pt.max || effective_weight <= pt.max) {
          myprice = pt;
          let n = pconf.prices![i - 1];
          rangeBottom = (n && n.max) ? n.max + 1 : 0;

          break;
        }
      }

      if (!myprice) return;

      let rangeStr = '';
      if (rangeTop && rangeBottom) {
        rangeStr = `${rangeBottom}-${rangeTop}lbs`;
      } else if (rangeTop && !rangeBottom) {
        rangeStr = `up to ${rangeTop}lbs`;
      } else if (rangeBottom && !rangeTop) {
        rangeStr = `${rangeBottom}+lbs`;
      }

      this.total = myprice.price;
      this.descriptor = `${rangeStr}`;

    } else if (pconf.pricetype === 'service_tiered' && pconf.sprices) {

      this.total = pconf.sprices[serviceTag || "_"] || 0;

    }

  }
}



export class OrderDataItemData {

  nameplate_lines?: string[] = [];
  engraving_type?: string = "";
  engraving_price?: number;


  constructor(data: any) {
    if (data.engraving_type) {
      this.engraving_type = data.engraving_type;
    }

    if (data.engraving_price) {
      this.engraving_price = parseInt(data.engraving_price);
    }

    if (data.nameplate_lines && Array.isArray(data.nameplate_lines)) {
      this.nameplate_lines = data.nameplate_lines;
    }
  }
}


export class OrderDataOStatus {

  pawprint_complete?: number | null;
  

  constructor(osdata?: any) {
    if (osdata) {
      this.pawprint_complete = osdata.pawprint_complete;
    }
  }

}


export class OrderHistoryEntry implements OrderHistoryEntryInterface {

  updated: number;
  admin_id: number;
  prev: OrderData;


  constructor(hdata: OrderHistoryEntryInterface) {
    this.updated = hdata.updated;
    this.admin_id = hdata.admin_id;
    this.prev = makeOrderData(hdata.prev, true);
  }

}


export interface OrderHistoryEntryInterface {
  updated: number;
  admin_id: number;
  prev: OrderData;
}
